import { Box, Skeleton, TableCell, TableRow } from '@mui/material';

import {
    INVENTORY_THUMB_PX,
    columnWidths,
    inventoryDataCellSx,
} from '../../lib/inventoryTableRowSpec';

interface OrderInventoryTableRowSkeletonProps {
    canSeePrices: boolean;
}

export const OrderInventoryTableRowSkeleton = ({
    canSeePrices,
}: OrderInventoryTableRowSkeletonProps) => {
    return (
        <TableRow
            data-testclass="inventory-item-row-skeleton"
            sx={{
                transform: 'translate(0)',
                clipPath: ' inset(0)',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
                borderRadius: '4px',
                '& td': {
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    '&:first-of-type': {
                        borderRadius: '4px 0 0 4px',
                    },
                    '&:last-of-type': {
                        borderRadius: '0 4px 4px 0',
                    },
                },
            }}
        >
            <TableCell
                padding="checkbox"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.thumbnail,
                    padding: '0 0 0 20px',
                }}
            >
                <Skeleton
                    variant="rectangular"
                    width={INVENTORY_THUMB_PX}
                    height={INVENTORY_THUMB_PX}
                    sx={{ borderRadius: '4px', flexShrink: 0 }}
                />
            </TableCell>
            <TableCell
                sx={{
                    ...inventoryDataCellSx,
                    wordWrap: 'break-word',
                    boxSizing: 'border-box',
                }}
            >
                <Box display="flex" flexDirection="column" gap="4px">
                    <Skeleton
                        variant="text"
                        height={24}
                        sx={{ width: 'min(280px, 100%)', maxWidth: '100%' }}
                    />
                    <Skeleton
                        variant="text"
                        height={20}
                        sx={{ width: 'min(160px, 80%)', maxWidth: '100%' }}
                    />
                </Box>
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.sku,
                    boxSizing: 'content-box',
                }}
            >
                <Skeleton
                    variant="rounded"
                    width={72}
                    height={22}
                    sx={{ mx: 'auto' }}
                />
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.qtyPerUom,
                    boxSizing: 'content-box',
                }}
            >
                <Skeleton
                    variant="rounded"
                    width={28}
                    height={22}
                    sx={{ mx: 'auto' }}
                />
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: '52px',
                    maxWidth: '52px',
                    boxSizing: 'content-box',
                }}
            >
                <Skeleton
                    variant="rounded"
                    width={36}
                    height={20}
                    sx={{ mx: 'auto' }}
                />
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.minOnHand,
                    boxSizing: 'content-box',
                }}
            >
                <Skeleton
                    variant="rounded"
                    width={32}
                    height={22}
                    sx={{ mx: 'auto' }}
                />
            </TableCell>
            {canSeePrices && (
                <TableCell
                    align="right"
                    sx={{
                        ...inventoryDataCellSx,
                        width: '84px',
                        maxWidth: '84px',
                        boxSizing: 'content-box',
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={56}
                        height={22}
                        sx={{ ml: 'auto' }}
                    />
                </TableCell>
            )}
            <TableCell
                align="right"
                sx={{
                    ...inventoryDataCellSx,
                    minWidth: columnWidths.buttons,
                }}
            >
                <Box display="flex" justifyContent="flex-end">
                    <Skeleton
                        variant="rounded"
                        width={88}
                        height={32}
                        sx={{ borderRadius: '4px' }}
                    />
                </Box>
            </TableCell>
        </TableRow>
    );
};
